import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './App.css'
import './Campaigns.css'
import { Link, Redirect, useHistory } from 'react-router-dom'
import Axios from 'axios'
import TopBarProgress from 'react-topbar-progress-indicator'
import { API_URL } from './config'
import Header from './layouts/Header'
import SideNav from './layouts/SideNav'
import {
  IoIosRefresh,
  IoIosArrowBack,
  IoIosArrowForward,
  IoIosClose,
  IoIosAdd,
  IoLogoAndroid,
  IoLogoWindows,
  IoLogoChrome,
} from 'react-icons/io'
import { IoPhonePortrait, IoInformationCircleOutline, IoSearchOutline } from 'react-icons/io5'
import { BsArrowRight, BsPen, BsGraphUp } from 'react-icons/bs'
import { BiDevices, BiMenu } from 'react-icons/bi'
import { RiMacbookLine } from 'react-icons/ri'
import { FaEdge, FaSafari, FaFirefox, FaLinux } from 'react-icons/fa'

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const ENDPOINT = `${API_URL}/apps/kscanner/webservices.php`
const PLACEHOLDER = `${API_URL}/apps/kscanner/placeholder.png`

const MOBILE_BREAKPOINT = 768
const PAGE_SIZE = 10
const PANEL_PAGE_SIZE = 8
const COLUMN_COUNT = 8

const PHYSICAL_NOTE = 'Assumes every printed ticket was sold. Actual door sales may differ.'
const ESTIMATE_NOTE = 'Based on the number of tickets loaded. Assumes every loaded ticket sells.'

const api = (query, params = {}) =>
  Axios.get(ENDPOINT, { params: { query, ...params }, withCredentials: true })

// Server sends some numbers pre-formatted ("1,234.00"), so strip separators first.
const num = (value) => {
  const parsed = parseFloat(String(value ?? '0').replace(/,/g, ''))
  return Number.isNaN(parsed) ? 0 : parsed
}

const money = (value) =>
  'R' + num(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const count = (value) => num(value).toLocaleString('en-US')

const todayISO = () => new Date().toLocaleDateString('en-CA') // YYYY-MM-DD, local time

// Accepts a plain array, or an object that wraps one ({ status, platforms: [...] })
const toList = (data) => {
  if (Array.isArray(data)) return data
  if (data && typeof data === 'object') {
    const key = Object.keys(data).find((k) => Array.isArray(data[k]))
    if (key) return data[key]
  }
  return []
}

const posterUrl = (businessId, eventId, file) =>
  !file || file.trim() === '' || file === 'none'
    ? PLACEHOLDER
    : `${API_URL}/apps/kscanner/posters/${businessId}/${eventId}/${file}`

const onImageError = (e) => {
  e.currentTarget.onerror = null
  e.currentTarget.src = PLACEHOLDER
}

const statusOf = (c) =>
  c.campaignstatus === 'running' ? 'running' : c.daysRemaining === '---' ? 'ended' : 'paused'

const STATUS_LABEL = { running: 'Running', paused: 'Paused', ended: 'Ended' }
const STATUS_TITLE = {
  running: 'This campaign is live',
  paused: 'Paused or not started yet',
  ended: 'The campaign period has ended',
}

const remainingText = (c) =>
  c.daysRemaining === '---'
    ? 'Period ended'
    : `${c.daysRemaining} ${Number(c.daysRemaining) === 1 ? 'day' : 'days'} left`

const deviceName = (device) => (device === 'MacIntel' ? 'Mac' : device)

const deviceIcon = (device) => {
  if (device === 'Android') return <IoLogoAndroid size={18} color="#3f9b5a" />
  if (device === 'Windows') return <IoLogoWindows size={18} color="#1664a8" />
  if (device === 'iPhone' || device === 'iPad') return <IoPhonePortrait size={18} color="#5b6b7f" />
  if (device === 'MacIntel' || device === 'Mac') return <RiMacbookLine size={18} color="#5b6b7f" />
  if (device === 'Linux') return <FaLinux size={18} color="#333" />
  return <BiDevices size={18} color="#5b6b7f" />
}

const browserIcon = (agent) => {
  if (agent === 'Edge') return <FaEdge size={18} color="#1664a8" />
  if (agent === 'Chrome') return <IoLogoChrome size={18} color="#d93025" />
  if (agent === 'Firefox') return <FaFirefox size={18} color="#e8710a" />
  if (agent === 'Safari') return <FaSafari size={18} color="#2f7fc1" />
  return <BiDevices size={18} color="#5b6b7f" />
}

// Tolerates a few likely column names so a backend rename doesn't blank the tab
const normaliseDevice = (r) => ({
  device: r.device ?? r.platform ?? r.os ?? 'Unknown',
  browser: r.useragent ?? r.browser ?? r.user_agent ?? 'Unknown',
  visits: num(r.platformCount ?? r.count ?? r.visits ?? r.total),
})

// State the /campaign and /analytics pages expect
const routeState = (c, fallbackBusinessId, extra = {}) => ({
  eventid: c.eventid,
  eventname: c.eventname,
  poster: posterUrl(c.businessid || fallbackBusinessId, c.eventid, c.thumbnail),
  eventdate: c.eventday,
  enddate: c.enddate,
  event_date: c.event_day,
  finish_date: c.finish_day,
  theme: c.theme,
  venue: c.venue,
  expectednum: c.expected,
  time: c.time,
  endtime: c.endtime,
  prevpage: 'campaigns',
  currentpage: 'campaigns',
  ...extra,
})

// Small "Est." tag shown next to any figure that is an estimate, not a recorded sale
const Est = ({ title }) => (
  <abbr className="cm-est" title={title}>
    Est.
  </abbr>
)

const Row = ({ label, hint, est, children }) => (
  <div>
    <dt>
      {label} {est && <Est title={est} />}
      {hint && <small>{hint}</small>}
    </dt>
    <dd>{children}</dd>
  </div>
)

const Pager = ({ page, pages, total, size, onChange }) => {
  if (total <= size) return null
  const from = (page - 1) * size + 1
  const to = Math.min(page * size, total)
  return (
    <div className="cm-pager">
      <span className="cm-pager-range">
        {from}–{to} of {total}
      </span>
      <div className="cm-pager-btns">
        <button
          type="button"
          className="cm-btn"
          onClick={() => onChange(page - 1)}
          disabled={page <= 1}
        >
          Previous
        </button>
        <span className="cm-pager-page">
          Page {page} of {pages}
        </span>
        <button
          type="button"
          className="cm-btn"
          onClick={() => onChange(page + 1)}
          disabled={page >= pages}
        >
          Next
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

const Campaignmanager = () => {
  const history = useHistory()

  const businessid = localStorage.getItem('businessid')
  const businessname = localStorage.getItem('businessname')
  const accounttype = localStorage.getItem('accounttype')

  const [details, setDetails] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [busy, setBusy] = useState(true)
  const [toast, setToast] = useState(null)
  const [isMob, setIsMob] = useState(window.innerWidth <= MOBILE_BREAKPOINT)
  const [collapsed, setCollapsed] = useState(window.innerWidth <= MOBILE_BREAKPOINT)
  const [page, setPage] = useState(1)
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')

  // drawer: null | { kind: 'campaign', campaignid, tab } | { kind: 'create' }
  const [drawer, setDrawer] = useState(null)
  const [tabs, setTabs] = useState({}) // { devices?: {status,data,message}, buyers?: {...} }
  const [create, setCreate] = useState({ status: 'idle', data: [] })
  const [createPage, setCreatePage] = useState(1)
  const [createQuery, setCreateQuery] = useState('')

  const reqs = useRef({ devices: 0, buyers: 0, create: 0 })
  const selectAllRef = useRef(null)

  /* ---------- derived ---------- */

  const selectedCount = useMemo(
    () => details.filter((c) => Number(c.selected) === 1).length,
    [details],
  )
  const allSelected = details.length > 0 && selectedCount === details.length

  const totals = useMemo(() => {
    const t = { online: 0, physical: 0, admissions: 0, running: 0 }
    details.forEach((c) => {
      t.online += num(c.digitalRevenue)
      t.physical += num(c.physicalRevenue)
      t.admissions += num(c.admission)
      if (c.campaignstatus === 'running') t.running += 1
    })
    return t
  }, [details])

  const statusCounts = useMemo(() => {
    const t = { all: details.length, running: 0, paused: 0, ended: 0 }
    details.forEach((c) => {
      t[statusOf(c)] += 1
    })
    return t
  }, [details])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return details.filter(
      (c) =>
        (filter === 'all' || statusOf(c) === filter) &&
        (!q ||
          `${c.campaignname || ''} ${c.eventname || ''} ${c.keywords || ''}`
            .toLowerCase()
            .includes(q)),
    )
  }, [details, filter, query])

  const pages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  const currentPage = Math.min(page, pages) // clamps if rows disappear
  const pagedDetails = visible.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const active =
    drawer && drawer.kind === 'campaign'
      ? details.find((c) => String(c.campaignid) === String(drawer.campaignid))
      : null

  /* ---------- data ---------- */

  const notify = useCallback((message, tone = 'info') => {
    setToast({ message, tone, id: Date.now() })
  }, [])

  const loadCampaigns = useCallback(() => {
    setBusy(true)
    return api('CAMPAIGNS', { businessid })
      .then((res) => {
        setDetails(Array.isArray(res.data) ? res.data : [])
        setLoadFailed(false)
      })
      .catch((err) => {
        console.log(err)
        setLoadFailed(true)
      })
      .finally(() => {
        setLoaded(true)
        setBusy(false)
      })
  }, [businessid])

  useEffect(() => {
    document.title = 'Campaigns | ' + businessname
    if (businessid === null) return
    loadCampaigns()
  }, [businessid, businessname, loadCampaigns])

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT
      setIsMob(mobile)
      setCollapsed((prev) => (mobile ? true : prev))
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!toast) return undefined
    const timer = setTimeout(() => setToast(null), 5000)
    return () => clearTimeout(timer)
  }, [toast])

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = selectedCount > 0 && !allSelected
    }
  }, [selectedCount, allSelected])

  // Close the drawer if its campaign no longer exists
  useEffect(() => {
    if (drawer && drawer.kind === 'campaign' && loaded && !active) setDrawer(null)
  }, [drawer, loaded, active])

  useEffect(() => {
    if (!drawer) return undefined
    const onKey = (e) => e.key === 'Escape' && setDrawer(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [drawer])

  /* ---------- actions ---------- */

  const runAction = (queryName, params, onDone) => {
    if (busy) return
    setBusy(true)
    api(queryName, params)
      .then((res) => onDone && onDone(res.data))
      .catch((err) => {
        console.log(err)
        notify("Couldn't complete that action. Check your connection and try again.", 'error')
      })
      .finally(loadCampaigns)
  }

  const toggleRunning = (c) =>
    runAction('RESUMECAMPAIGN', { eventid: c.eventid, businessid }, (data) => {
      if (!data) return
      if (data.status === 'failed') notify(data.message, 'error')
      else if (data.message) notify(data.message, 'success')
    })

  const toggleSelected = (c) =>
    runAction('UPDATESELECT', { eventid: c.eventid, accountid: businessid })

  const toggleSelectAll = () => runAction('UPDATESELECT', { businessid })

  const resumeSelected = () => runAction('RESUMECAMPAIGNS', { businessid })

  const goEdit = (c) => history.push({ pathname: '/campaign', state: routeState(c, businessid) })

  const goAnalytics = (c) =>
    history.push({
      pathname: '/analytics',
      state: routeState(c, businessid, { back: 'campaigns' }),
    })

  const closeDrawer = () => {
    reqs.current.devices += 1
    reqs.current.buyers += 1
    reqs.current.create += 1
    setDrawer(null)
  }

  const loadTab = (tab, c) => {
    const req = ++reqs.current[tab]
    setTabs((prev) => ({ ...prev, [tab]: { status: 'loading', data: [] } }))

    const call =
      tab === 'devices'
        ? api('PLATFORMS', { businessid, eventid: c.eventid, campaignid: c.campaignid })
        : api('CLIENTELE', { subquery: 'GETCLIENTELE', eventid: c.eventid, businessid })

    call
      .then((res) => {
        if (req !== reqs.current[tab]) return
        const body = res.data
        if (body && !Array.isArray(body) && body.status === 'failed') {
          setTabs((prev) => ({
            ...prev,
            [tab]: { status: 'error', data: [], message: body.message },
          }))
          return
        }
        const list = toList(body)
        if (list.length === 0) console.log(`${tab} returned no rows. Raw response:`, body)
        setTabs((prev) => ({ ...prev, [tab]: { status: 'ready', data: list } }))
      })
      .catch((err) => {
        console.log(err)
        if (req !== reqs.current[tab]) return
        setTabs((prev) => ({ ...prev, [tab]: { status: 'error', data: [] } }))
      })
  }

  const openCampaign = (c) => {
    reqs.current.devices += 1
    reqs.current.buyers += 1
    setTabs({})
    setDrawer({ kind: 'campaign', campaignid: c.campaignid, tab: 'overview' })
  }

  const switchTab = (tab) => {
    if (!active) return
    setDrawer((d) => ({ ...d, tab }))
    const state = tabs[tab]
    if (tab !== 'overview' && (!state || state.status === 'error')) loadTab(tab, active)
  }

  const openCreate = () => {
    const req = ++reqs.current.create
    setCreatePage(1)
    setCreateQuery('')
    setCreate({ status: 'loading', data: [] })
    setDrawer({ kind: 'create' })

    api('MYEVENTS', { accountid: businessid, accounttype, thumbnail: 'yes' })
      .then((res) => {
        if (req !== reqs.current.create) return
        setCreate({ status: 'ready', data: toList(res.data) })
      })
      .catch((err) => {
        console.log(err)
        if (req !== reqs.current.create) return
        setCreate({ status: 'error', data: [] })
      })
  }

  const handleToggleSidebar = () => setCollapsed((prev) => !prev)

  /* ---------- guards ---------- */

  if (businessid === null) {
    return <Redirect to="/login" />
  }

  /* ---------- render pieces ---------- */

  const subtitle = !loaded
    ? 'Loading your campaigns'
    : details.length === 0
      ? 'Promote an event to start selling tickets'
      : `${details.length} ${details.length === 1 ? 'campaign' : 'campaigns'}, ${totals.running} running`

  const renderRow = (c) => {
    const status = statusOf(c)
    const isSel = Number(c.selected) === 1
    const online = num(c.digitalRevenue)
    const physical = num(c.physicalRevenue)

    return (
      <tr key={c.campaignid} className={isSel ? 'is-selected' : ''}>
        <td className="cm-c-check">
          <input
            type="checkbox"
            className="cm-check"
            checked={isSel}
            onChange={() => toggleSelected(c)}
            aria-label={`Select ${c.campaignname}`}
          />
        </td>

        <td className="cm-name-cell" data-label="Campaign">
          <button type="button" className="cm-name" onClick={() => openCampaign(c)}>
            <img
              className="cm-poster"
              src={posterUrl(c.businessid || businessid, c.eventid, c.thumbnail)}
              onError={onImageError}
              alt=""
            />
            <span className="cm-name-text">
              <span className="cm-name-link" title={c.campaignname}>
                {c.campaignname}
              </span>
              <span className="cm-muted" title={c.eventname}>
                {c.eventname}
              </span>
            </span>
          </button>
        </td>

        <td data-label="Status">
          <div className="cm-status">
            <button
              type="button"
              role="switch"
              aria-checked={status === 'running'}
              aria-label={`${status === 'running' ? 'Pause' : 'Resume'} ${c.campaignname}`}
              className={`cm-switch ${status === 'running' ? 'on' : ''}`}
              onClick={() => toggleRunning(c)}
              disabled={busy}
            >
              <span />
            </button>
            <span className={`cm-badge ${status}`} title={STATUS_TITLE[status]}>
              {STATUS_LABEL[status]}
            </span>
          </div>
        </td>

        <td data-label="Running period">
          <span className="cm-strong">
            {c.fromdate} – {c.todate}
          </span>
          <span className="cm-muted">{remainingText(c)}</span>
        </td>

        <td className="is-num num" data-label="Revenue">
          <span className="cm-strong">{money(online + physical)}</span>
          <span className="cm-muted">{money(online)} online</span>
          <span className="cm-muted">
            {money(physical)} physical <Est title={PHYSICAL_NOTE} />
          </span>
        </td>

        <td className="is-num num" data-label="Admissions">
          <span className="cm-strong">{count(c.admission)}</span>
          <span className="cm-muted">{count(c.buyers)} unique buyers</span>
        </td>

        <td className="is-num num" data-label="Reach">
          <span className="cm-strong">{count(c.views)} views</span>
          <span className="cm-muted">{count(c.eventClicks)} clicks</span>
        </td>

        <td className="cm-open-cell">
          <button
            type="button"
            className="cm-open"
            onClick={() => openCampaign(c)}
            aria-label={`Open details for ${c.campaignname}`}
          >
            Details <IoIosArrowForward size={16} />
          </button>
        </td>
      </tr>
    )
  }

  const renderTableBody = () => {
    if (!loaded) {
      return (
        <tr>
          <td colSpan={COLUMN_COUNT}>
            <div className="cm-empty">
              <p>Loading campaigns</p>
            </div>
          </td>
        </tr>
      )
    }

    if (loadFailed) {
      return (
        <tr>
          <td colSpan={COLUMN_COUNT}>
            <div className="cm-empty">
              <h3>We couldn't load your campaigns</h3>
              <p>Check your connection, then try again.</p>
              <button type="button" className="cm-btn cm-btn-primary" onClick={loadCampaigns}>
                Try again
              </button>
            </div>
          </td>
        </tr>
      )
    }

    if (details.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMN_COUNT}>
            <div className="cm-empty">
              <h3>No campaigns yet</h3>
              <p>Create a campaign to start promoting an event and selling tickets.</p>
              <button type="button" className="cm-btn cm-btn-primary" onClick={openCreate}>
                <IoIosAdd size={20} /> Create campaign
              </button>
            </div>
          </td>
        </tr>
      )
    }

    if (visible.length === 0) {
      return (
        <tr>
          <td colSpan={COLUMN_COUNT}>
            <div className="cm-empty">
              <h3>No campaigns match</h3>
              <p>Try a different search or status filter.</p>
              <button
                type="button"
                className="cm-btn"
                onClick={() => {
                  setFilter('all')
                  setQuery('')
                }}
              >
                Clear filters
              </button>
            </div>
          </td>
        </tr>
      )
    }

    return pagedDetails.map(renderRow)
  }

  /* ----- drawer: campaign ----- */

  const renderDevices = () => {
    const state = tabs.devices
    if (!state || state.status === 'loading') return <p className="cm-drawer-note">Loading</p>
    if (state.status === 'error')
      return (
        <div className="cm-drawer-note">
          <p>{state.message || "We couldn't load device data."}</p>
          <button type="button" className="cm-btn" onClick={() => loadTab('devices', active)}>
            Try again
          </button>
        </div>
      )

    const rows = state.data.map(normaliseDevice).sort((a, b) => b.visits - a.visits)
    if (rows.length === 0)
      return (
        <div className="cm-drawer-note">
          <p>
            No device data for this campaign yet. It fills in as people open the event page from
            this campaign.
          </p>
          <button type="button" className="cm-btn" onClick={() => loadTab('devices', active)}>
            Refresh
          </button>
        </div>
      )

    const total = rows.reduce((sum, r) => sum + r.visits, 0)
    return (
      <>
        <p className="cm-tab-total">
          <strong>{count(total)}</strong> {total === 1 ? 'visit' : 'visits'}
        </p>
        <ul className="cm-bars">
          {rows.map((r, i) => {
            const share = total > 0 ? (r.visits / total) * 100 : 0
            return (
              <li key={`${r.device}-${r.browser}-${i}`}>
                <div className="cm-bars-head">
                  <span className="with-icon">
                    {deviceIcon(r.device)}
                    <strong>{deviceName(r.device)}</strong>
                  </span>
                  <span className="with-icon cm-muted">
                    {browserIcon(r.browser)}
                    {r.browser}
                  </span>
                  <span className="cm-bars-count num">
                    {count(r.visits)} <small>{Math.round(share)}%</small>
                  </span>
                </div>
                <div className="cm-bar">
                  <span style={{ width: `${share}%` }} />
                </div>
              </li>
            )
          })}
        </ul>
      </>
    )
  }

  const renderBuyers = () => {
    const state = tabs.buyers
    if (!state || state.status === 'loading') return <p className="cm-drawer-note">Loading</p>
    if (state.status === 'error')
      return (
        <div className="cm-drawer-note">
          <p>{state.message || "We couldn't load buyers."}</p>
          <button type="button" className="cm-btn" onClick={() => loadTab('buyers', active)}>
            Try again
          </button>
        </div>
      )
    if (state.data.length === 0)
      return <p className="cm-drawer-note">No one has bought tickets for this campaign yet.</p>

    return (
      <>
        <p className="cm-tab-total">
          <strong>{count(state.data.length)}</strong> unique{' '}
          {state.data.length === 1 ? 'buyer' : 'buyers'}
        </p>
        <ul className="cm-list">
          {state.data.map((item) => (
            <li key={item.clientid}>
              <Link
                className="cm-row"
                to={{
                  pathname: '/customerprofile',
                  state: {
                    accountid: businessid,
                    accounttype,
                    clientid: item.clientid,
                    phone: item.phone,
                    clientename: item.fname + ' ' + item.lname,
                    email: item.email,
                    prevpage: 'customers',
                  },
                }}
              >
                <div className="cm-row-main">
                  <strong>
                    {item.fname} {item.lname}
                  </strong>
                  <span className="cm-muted">{item.email || item.phone}</span>
                </div>
                <BsArrowRight />
              </Link>
            </li>
          ))}
        </ul>
      </>
    )
  }

  const renderOverview = (c) => {
    const online = num(c.digitalRevenue)
    const physical = num(c.physicalRevenue)
    const fee = num(c.feesDeductedPhysical) + num(c.feesDeductedDigital)

    return (
      <>
        <h3 className="cm-sec">Revenue</h3>
        <dl className="cm-rows">
          <Row label="Online revenue" hint="From completed ticket payments">
            {money(online)}
          </Row>
          <Row label="Physical revenue" est={PHYSICAL_NOTE} hint={PHYSICAL_NOTE}>
            {money(physical)}
          </Row>
          <Row label="Total revenue" hint="Online plus estimated physical">
            {money(online + physical)}
          </Row>
          <Row label="Platform fee" hint="3.5% commission on ticket sales, deducted before payout">
            {money(fee)}
          </Row>
          <Row label="Estimated revenue" est={ESTIMATE_NOTE} hint={ESTIMATE_NOTE}>
            {money(c.estdRevenue)}
          </Row>
        </dl>

        <h3 className="cm-sec">Audience</h3>
        <dl className="cm-rows">
          <Row label="Admissions" hint="People expected to attend, based on tickets sold">
            {count(c.admission)}
          </Row>
          <Row label="Unique buyers">{count(c.buyers)}</Row>
          <Row label="Views">{count(c.views)}</Row>
          <Row label="Clicks">{count(c.eventClicks)}</Row>
          <Row label="Unique visitors" hint="Distinct people who viewed the event">
            {count(c.unique_visitors)}
          </Row>
          <Row label="Unique clicks" hint="Distinct people who clicked, counted once">
            {count(c.unique_clicks)}
          </Row>
        </dl>

        <h3 className="cm-sec">Campaign</h3>
        <dl className="cm-rows">
          <Row label="Event">{c.eventname}</Row>
          <Row label="Event date">{c.eventday}</Row>
          <Row label="Starts">{c.fromdate}</Row>
          <Row label="Ends">{c.todate}</Row>
          <Row label="Running period">{c.getPeriod} days</Row>
          <Row label="Keywords" hint="Used to promote this campaign">
            {c.keywords || 'None'}
          </Row>
        </dl>
      </>
    )
  }

  const renderCampaignDrawer = (c) => {
    const status = statusOf(c)
    const tab = drawer.tab
    return (
      <>
        <div className="cm-drawer-head">
          <div className="cm-drawer-title">
            <img
              className="cm-poster"
              src={posterUrl(c.businessid || businessid, c.eventid, c.thumbnail)}
              onError={onImageError}
              alt=""
            />
            <div>
              <h2>{c.campaignname}</h2>
              <p>
                <span className={`cm-badge ${status}`}>{STATUS_LABEL[status]}</span>
                <span className="cm-muted">{remainingText(c)}</span>
              </p>
            </div>
          </div>
          <button
            type="button"
            className="cm-btn cm-btn-icon"
            onClick={closeDrawer}
            aria-label="Close panel"
            autoFocus
          >
            <IoIosClose size={26} />
          </button>
        </div>

        <div className="cm-tabs" role="tablist">
          {[
            ['overview', 'Overview'],
            ['devices', 'Devices'],
            ['buyers', 'Buyers'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              className={`cm-tab ${tab === key ? 'is-active' : ''}`}
              onClick={() => switchTab(key)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="cm-drawer-body">
          {tab === 'overview' && renderOverview(c)}
          {tab === 'devices' && renderDevices()}
          {tab === 'buyers' && renderBuyers()}
        </div>

        <div className="cm-drawer-foot">
          <button type="button" className="cm-btn" onClick={() => goEdit(c)}>
            <BsPen /> Edit
          </button>
          <button type="button" className="cm-btn" onClick={() => goAnalytics(c)}>
            <BsGraphUp /> Analytics
          </button>
          {status !== 'ended' && (
            <button
              type="button"
              className="cm-btn cm-btn-primary"
              onClick={() => toggleRunning(c)}
              disabled={busy}
            >
              {status === 'running' ? 'Pause campaign' : 'Resume campaign'}
            </button>
          )}
        </div>
      </>
    )
  }

  /* ----- drawer: create ----- */

  const renderCreateBody = () => {
    if (create.status === 'loading') return <p className="cm-drawer-note">Loading</p>
    if (create.status === 'error')
      return (
        <div className="cm-drawer-note">
          <p>We couldn't load your events.</p>
          <button type="button" className="cm-btn" onClick={openCreate}>
            Try again
          </button>
        </div>
      )
    if (create.data.length < 1)
      return (
        <p className="cm-drawer-note">
          No events are ready for a campaign. Create an event and upload its poster first.
        </p>
      )

    const today = todayISO()
    const q = createQuery.trim().toLowerCase()
    const filtered = q
      ? create.data.filter((item) =>
          `${item.eventname || ''} ${item.event_day || ''} ${item.venue || ''}`
            .toLowerCase()
            .includes(q),
        )
      : create.data
    const createPages = Math.max(1, Math.ceil(filtered.length / PANEL_PAGE_SIZE))
    const createCurrent = Math.min(createPage, createPages)
    const visibleEvents = filtered.slice(
      (createCurrent - 1) * PANEL_PAGE_SIZE,
      createCurrent * PANEL_PAGE_SIZE,
    )

    return (
      <>
        <div className="cm-search cm-search-block">
          <IoSearchOutline size={18} />
          <input
            type="search"
            placeholder="Search events by name, date or venue"
            value={createQuery}
            onChange={(e) => {
              setCreateQuery(e.target.value)
              setCreatePage(1)
            }}
            aria-label="Search events"
          />
        </div>

        {filtered.length < 1 ? (
          <p className="cm-drawer-note">No events match "{createQuery.trim()}".</p>
        ) : (
          <ul className="cm-list">
            {visibleEvents.map((item) => {
              const past = item.event_day < today
              const inner = (
                <>
                  <img
                    className="cm-poster"
                    src={posterUrl(item.businessid || businessid, item.eventid, item.thumbnail)}
                    onError={onImageError}
                    alt=""
                  />
                  <div className="cm-row-main">
                    <strong>{item.eventname}</strong>
                    <span className="cm-muted">
                      {past ? `${item.event_day}, the event date has passed` : item.event_day}
                    </span>
                  </div>
                  {!past && <BsArrowRight />}
                </>
              )
              return (
                <li key={item.eventid}>
                  {past ? (
                    <div className="cm-row is-past">{inner}</div>
                  ) : (
                    <Link
                      className="cm-row"
                      to={{
                        pathname: '/campaign',
                        state: {
                          eventid: item.eventid,
                          eventname: item.eventname,
                          poster: posterUrl(
                            item.businessid || businessid,
                            item.eventid,
                            item.thumbnail,
                          ),
                          eventdate: item.eventday,
                          enddate: item.enddate,
                          event_date: item.event_day,
                          finish_date: item.finish_day,
                          theme: item.theme,
                          venue: item.venue,
                          expectednum: item.expected,
                          time: item.time,
                          endtime: item.endtime,
                          prevpage: 'campaigns',
                          currentpage: 'campaigns',
                        },
                      }}
                    >
                      {inner}
                    </Link>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        <Pager
          page={createCurrent}
          pages={createPages}
          total={filtered.length}
          size={PANEL_PAGE_SIZE}
          onChange={setCreatePage}
        />
      </>
    )
  }

  const renderCreateDrawer = () => (
    <>
      <div className="cm-drawer-head">
        <div>
          <h2>Choose an event</h2>
          <p>Pick the event you want to promote.</p>
        </div>
        <button
          type="button"
          className="cm-btn cm-btn-icon"
          onClick={closeDrawer}
          aria-label="Close panel"
          autoFocus
        >
          <IoIosClose size={26} />
        </button>
      </div>
      <div className="cm-drawer-body">{renderCreateBody()}</div>
      <div className="cm-drawer-foot">
        <Link
          className="cm-btn cm-btn-primary cm-btn-block"
          to={{ pathname: '/addevent', state: { prevpage: 'dashboard' } }}
        >
          <IoIosAdd size={20} /> Create a new event
        </Link>
      </div>
    </>
  )

  /* ---------- layout ---------- */

  const FILTERS = [
    ['all', 'All'],
    ['running', 'Running'],
    ['paused', 'Paused'],
    ['ended', 'Ended'],
  ]

  return (
    <div className="view">
      {busy && <TopBarProgress />}
      <Header />
      <SideNav collapsed={collapsed} onToggle={handleToggleSidebar} />

      <div
        className="main-content"
        style={{
          marginTop: 'var(--header-h, 72px)',
          marginLeft: isMob ? 0 : collapsed ? 76 : 252,
          maxWidth: 'none',
        }}
      >
        {isMob && !collapsed && (
          <div className="mobile-overlay active" onClick={handleToggleSidebar} />
        )}

        {isMob && collapsed && (
          <label className="mobile-toggle" onClick={handleToggleSidebar}>
            <BiMenu size={25} />
          </label>
        )}

        <div className="cm">
          {/* Page header */}
          <div className="cm-top">
            <div>
              <button type="button" className="cm-back" onClick={() => history.goBack()}>
                <IoIosArrowBack size={18} /> Back
              </button>
              <h1 className="cm-title">Campaigns</h1>
              <p className="cm-sub">{subtitle}</p>
            </div>
            <div className="cm-top-actions">
              <button
                type="button"
                className={`cm-btn cm-btn-icon ${busy ? 'cm-spin' : ''}`}
                onClick={loadCampaigns}
                disabled={busy}
                aria-label="Refresh campaigns"
                title="Refresh"
              >
                <IoIosRefresh size={20} />
              </button>
              <button type="button" className="cm-btn cm-btn-primary" onClick={openCreate}>
                <IoIosAdd size={20} /> Create campaign
              </button>
            </div>
          </div>

          {details.length > 0 && (
            <>
              {/* Summary */}
              <div className="cm-band num">
                <div className="cm-card-stat is-lead">
                  <span className="cm-band-label">Total revenue</span>
                  <span className="cm-band-value">{money(totals.online + totals.physical)}</span>
                  <span className="cm-band-note">Online plus estimated physical</span>
                </div>
                <div className="cm-card-stat">
                  <span className="cm-band-label">Online revenue</span>
                  <span className="cm-band-value">{money(totals.online)}</span>
                  <span className="cm-band-note">From completed payments</span>
                </div>
                <div className="cm-card-stat">
                  <span className="cm-band-label">
                    Physical revenue <Est title={PHYSICAL_NOTE} />
                  </span>
                  <span className="cm-band-value">{money(totals.physical)}</span>
                  <span className="cm-band-note">Assumes all printed tickets sold</span>
                </div>
                <div className="cm-card-stat">
                  <span className="cm-band-label">Admissions</span>
                  <span className="cm-band-value">{count(totals.admissions)}</span>
                  <span className="cm-band-note">Across all campaigns</span>
                </div>
                <div className="cm-card-stat">
                  <span className="cm-band-label">Running now</span>
                  <span className="cm-band-value">{totals.running}</span>
                  <span className="cm-band-note">of {details.length}</span>
                </div>
              </div>

              {/* How the numbers work */}
              <div className="cm-notice" role="note">
                <IoInformationCircleOutline size={20} />
                <p>
                  <strong>Online revenue</strong> is from completed payments.{' '}
                  <strong>Physical revenue</strong> is an estimate: it assumes every printed ticket
                  was sold. <strong>Estimated revenue</strong> is based on the tickets you loaded
                  and assumes they all sell.
                </p>
              </div>

              {/* Filters */}
              <div className="cm-filters">
                <div className="cm-chips" role="group" aria-label="Filter by status">
                  {FILTERS.map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      className={`cm-chip ${filter === key ? 'is-active' : ''}`}
                      aria-pressed={filter === key}
                      onClick={() => setFilter(key)}
                    >
                      {label} <span>{statusCounts[key]}</span>
                    </button>
                  ))}
                </div>
                <div className="cm-search">
                  <IoSearchOutline size={18} />
                  <input
                    type="search"
                    placeholder="Search campaigns"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search campaigns"
                  />
                </div>
              </div>
            </>
          )}

          {/* Bulk bar */}
          {selectedCount > 1 && (
            <div className="cm-selbar" role="region" aria-label="Selected campaigns">
              <div className="cm-selbar-text">{selectedCount} campaigns selected</div>
              <button type="button" className="cm-btn" onClick={resumeSelected} disabled={busy}>
                Pause or resume all selected
              </button>
            </div>
          )}

          {/* Campaigns table */}
          <div className="cm-card">
            <div className="cm-scroll">
              <table className="cm-table">
                <thead>
                  <tr>
                    <th className="cm-c-check">
                      <input
                        ref={selectAllRef}
                        type="checkbox"
                        className="cm-check"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        disabled={details.length === 0}
                        aria-label="Select all campaigns"
                      />
                    </th>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Running period</th>
                    <th className="is-num">Revenue</th>
                    <th className="is-num" title="People expected to attend, based on tickets sold">
                      Admissions
                    </th>
                    <th className="is-num">Reach</th>
                    <th>
                      <span className="cm-sr">Details</span>
                    </th>
                  </tr>
                </thead>
                <tbody>{renderTableBody()}</tbody>
              </table>
            </div>
            <Pager
              page={currentPage}
              pages={pages}
              total={visible.length}
              size={PAGE_SIZE}
              onChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* Side drawer */}
      {drawer && (drawer.kind === 'create' || active) && (
        <div className="cm-scrim cm-root" onClick={closeDrawer}>
          <aside
            className="cm-drawer"
            role="dialog"
            aria-modal="true"
            aria-label={drawer.kind === 'create' ? 'Choose an event' : active.campaignname}
            onClick={(e) => e.stopPropagation()}
          >
            {drawer.kind === 'create' ? renderCreateDrawer() : renderCampaignDrawer(active)}
          </aside>
        </div>
      )}

      {toast && (
        <div className="cm-root">
          <div
            key={toast.id}
            className={`cm-toast ${toast.tone}`}
            role="status"
            onClick={() => setToast(null)}
          >
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}

export default Campaignmanager
